// Temsilci Detayı — tek temsilci, tek gün: haftalık istatistik + çağrı günlüğü + DARK karşılaştırma.
// (Gün zaman çizelgesi 2026-09-13'te sahibin isteğiyle kaldırıldı; API day.timeline'ı hâlâ döner.)

import { useEffect, useRef, useState } from 'react';
import { ApiError, getAgentDay, getAgentWeek, getStatusSystems } from '../api';
import {
  brandLabel, hasDarkBrand, INBOUND_DUTY_META, SHIFT_META, smsOutcomeColor, smsOutcomeLabel,
  smsReasonLabel, smsTemplateColor, smsTemplateLabel, tzOfBrands,
} from '../constants';
import { downloadCSV, fmtClock, fmtDate, fmtHMS, fmtMoney, fmtMS, fmtPct1, fmtRange, fmtTalkLong, joinDot, todayInTz } from '../format';
import { refreshRoster, useDocumentClick, useEscape, useRoster } from '../hooks';
import { CsvButton, dateInputStyle, dotStyle, EmptyState, Icon, Skeleton, XlsxButton } from '../ui';
import { downloadXLSX } from '../xlsx-export';
import type { AgentDayDetail, AgentFocus, AgentWeek, BrandId, CallLogEntry, CyclePercentiles } from '../types';

interface StatusView {
  label: string;
  color: string;
}

type WeekState = 'loading' | 'ok' | 'busy' | 'error';

const WEEKDAY_TR = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'];

const weekTh: React.CSSProperties = {
  fontSize: '10px',
  color: 'var(--faint)',
  letterSpacing: '.05em',
  fontWeight: 600,
  padding: '8px 10px',
  textAlign: 'right',
  whiteSpace: 'nowrap',
  borderBottom: '1px solid var(--border)',
};
const weekTd: React.CSSProperties = {
  fontSize: '12.5px',
  color: 'var(--text)',
  padding: '8px 10px',
  textAlign: 'right',
  fontVariantNumeric: 'tabular-nums',
  whiteSpace: 'nowrap',
  borderBottom: '1px solid var(--border)',
};

/** "1.250 (3)" — tutar + kaç görüşmede para geldi; tutar yoksa sayı da basılmaz (§6). */
function moneyCell(amount: number, count: number): string {
  const m = fmtMoney(amount);
  return m ? m + (count ? ' (' + count + ')' : '') : '';
}

/** Görüşme → yatırım oranı: yatırım yapan / görüşme, haftalık raporun BAŞARI ORANI'yla aynı
 *  tanım (engine `pct`, 1 ondalık). Görüşme yoksa oran TANIMSIZ → boş (§6) — 0 basılmaz,
 *  "%0,0" YALNIZ görüşme var ama kimse yatırmadı demektir (§19: 0 gerçek cevaptır). */
/** BAŞARI = yatıran ÷ (görüşme − DAVET) — karnenin paydası (görev #297); eski sunucu `successBase` göndermez → görüşme. */
function rateCell(deposited: number, s: { conversations: number; successBase?: number }): string {
  const base = typeof s.successBase === 'number' ? s.successBase : s.conversations;
  if (!base) return '';
  return fmtPct1(Math.round((1000 * deposited) / base) / 10);
}

/** Kapatan taraf (call_sessions.hangup_by) → TR etiket; bilinmeyen değer olduğu gibi. */
const HANGUP_LABEL: Record<string, string> = { agent: 'temsilci', remote: 'müşteri', system: 'sistem' };

/** Çağrı günlüğü satırının ÖLÇÜLEN çağrı ayrıntısı (title). Oturum yoksa '' — title basılmaz.
 *  Parçalar " · " ile TEK dizge (i18n translatePhrase etiketleri parça parça çevirir). */
function sessionTitle(c: CallLogEntry, tz: string): string {
  if (!c.dialAt && !c.answeredAt && !c.endedAt) return '';
  return joinDot(
    c.dialAt ? 'Arandı ' + fmtClock(c.dialAt, tz, true) : '',
    c.answeredAt ? 'Cevaplandı ' + fmtClock(c.answeredAt, tz, true) : '',
    c.endedAt ? 'Bitti ' + fmtClock(c.endedAt, tz, true) : '',
    c.ringSec != null ? 'Çalma ' + fmtHMS(c.ringSec) : '',
    c.hangupBy ? 'Kapatan ' + (HANGUP_LABEL[c.hangupBy] || c.hangupBy) : '',
    c.cause ? 'Sebep ' + c.cause : '',
  );
}

/** ms → "1,8 sn" (bir ondalık; ≥ 60 sn "d:ss"). Dinamik → i18n regex çevirir. */
function fmtCycleMs(ms: number): string {
  if (ms >= 60_000) return fmtHMS(ms / 1000);
  return (Math.round(ms / 100) / 10).toFixed(1).replace('.', ',') + ' sn';
}

/** Döngü kartı satırı; metrik ölçülmediyse (null) satır HİÇ basılmaz (§6). */
function CycleRow({ label, p }: { label: string; p: CyclePercentiles | null }) {
  if (!p) return null;
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr 62px 62px 42px',
        gap: '6px',
        padding: '9px 16px',
        borderBottom: '1px solid var(--border)',
        alignItems: 'center',
      }}
    >
      <span style={{ fontSize: '12px', color: 'var(--text)' }}>{label}</span>
      <span style={{ textAlign: 'right', fontSize: '12px', color: 'var(--text)', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
        {fmtCycleMs(p.p50Ms)}
      </span>
      <span style={{ textAlign: 'right', fontSize: '12px', color: 'var(--dim)', fontVariantNumeric: 'tabular-nums' }}>{fmtCycleMs(p.p95Ms)}</span>
      <span style={{ textAlign: 'right', fontSize: '12px', color: 'var(--dim)', fontVariantNumeric: 'tabular-nums' }}>{p.n}</span>
    </div>
  );
}

/**
 * Haftalık istatistik — seçili günü içeren Pzt–Paz haftası, gün başına bir satır.
 * ARAMA / YANITLANAN bu app'in Postgres'inden (üst karttaki TOPLAM ÇAĞRI ile aynı kaynak);
 * GÖRÜŞME / YATIRIM haftalık raporun sheet taramasından (§19 — para DB'de yok). İkinci
 * kaynak yoksa (marka kapsam dışı / rapor meşgul / hata / ad eşleşmedi) o sütunlar BOŞ
 * kalır ve altta tek satırlık sebep yazılır — sıfır basılmaz, bu raporda 0 gerçek cevaptır.
 */
function WeekCard({
  week,
  weekState,
  selectedDay,
  onRetry,
}: {
  week: AgentWeek | null;
  weekState: WeekState;
  selectedDay: string;
  onRetry: () => void;
}) {
  const sheetOk = !!week && week.sheet.status === 'ok';
  const sheetNote = (() => {
    if (weekState === 'loading' && !week) return '';
    if (weekState === 'busy') return 'Haftalık rapor şu an başka bir hafta için hesaplanıyor — görüşme ve yatırım sütunları birazdan gelir.';
    if (weekState === 'error') return 'Haftalık veri alınamadı.';
    if (!week) return '';
    switch (week.sheet.status) {
      case 'ok':
        return week.sheet.settled ? '' : 'Yatırım tutarları henüz kesinleşmedi (48 saatlik pencereler açık) — rakamlar değişebilir.';
      case 'not_in_report':
        return 'Bu temsilcinin adı haftalık raporda bulunamadı — Ayarlar\'dan takma adını ekleyin.';
      case 'excluded':
        return 'Bu temsilci haftalık rapordan hariç tutuluyor (çağrı ekibinde değil).';
      case 'unsupported':
        return 'Görüşme ve yatırım verisi yalnız Vegas ve Prime için gelir.';
      case 'busy':
        return 'Haftalık rapor şu an başka bir hafta için hesaplanıyor — görüşme ve yatırım sütunları birazdan gelir.';
      case 'error':
        return 'Haftalık rapor okunamadı — görüşme ve yatırım sütunları boş.';
      default:
        return '';
    }
  })();
  const canRetry = weekState === 'busy' || weekState === 'error' || (!!week && (week.sheet.status === 'busy' || week.sheet.status === 'error'));

  return (
    <div
      style={{
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: '14px',
        padding: '16px 18px',
        marginBottom: '16px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '10px', flexWrap: 'wrap' }}>
        <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text)' }}>Haftalık istatistik</div>
        {week && (
          <div style={{ fontSize: '11.5px', color: 'var(--faint)' }}>{fmtRange(fmtDate(week.weekStart), fmtDate(week.weekEnd))}</div>
        )}
        <div style={{ marginLeft: 'auto', fontSize: '11px', color: 'var(--dim)', display: 'flex', gap: '14px', flexWrap: 'wrap' }}>
          <span>Arama · Yanıtlanan: panel</span>
          <span>Görüşme · Yatırım · Başarı: haftalık rapor</span>
        </div>
      </div>

      {!week && weekState === 'loading' && <Skeleton heights={[36, 200]} radius={10} gap={8} />}

      {week && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ ...weekTh, textAlign: 'left' }}>GÜN</th>
                <th style={weekTh}>ARAMA</th>
                <th style={weekTh}>YANITLANAN</th>
                <th style={weekTh} title="Softphone: bağlanan çağrıların konuşma süresi">KONUŞMA</th>
                <th style={weekTh}>GÖRÜŞME</th>
                <th style={weekTh}>YATIRIM 24S</th>
                <th style={weekTh} title="Yatırım yapan / görüşme (DAVET hariç)">BAŞARI 24S</th>
                <th style={weekTh}>YATIRIM 48S</th>
                <th style={weekTh} title="Yatırım yapan / görüşme (DAVET hariç)">BAŞARI 48S</th>
              </tr>
            </thead>
            <tbody>
              {week.days.map((d, i) => {
                const isSel = d.day === selectedDay;
                const sh = sheetOk ? d.sheet : null;
                return (
                  <tr key={d.day} style={{ background: isSel ? 'var(--accent-soft)' : 'transparent' }}>
                    <td style={{ ...weekTd, textAlign: 'left', color: isSel ? 'var(--text)' : 'var(--dim)' }}>
                      <span style={{ fontWeight: 600 }}>{WEEKDAY_TR[i]}</span>
                      <span style={{ marginLeft: '8px', color: 'var(--faint)' }}>{fmtDate(d.day).slice(0, 5)}</span>
                    </td>
                    <td style={weekTd}>{d.calls}</td>
                    <td style={weekTd}>{d.answered}</td>
                    {/* Softphone ölçümü: o gün oturum yoksa null → BOŞ (0 değil, §6). */}
                    <td
                      style={{ ...weekTd, color: 'var(--dim)' }}
                      title={d.dials != null ? 'Arama ' + d.dials + ' · bağlanan ' + (d.connected ?? 0) : undefined}
                    >
                      {d.talkSec != null ? fmtHMS(d.talkSec) : ''}
                    </td>
                    <td style={weekTd}>{sh ? sh.conversations : ''}</td>
                    <td style={weekTd}>{sh ? moneyCell(sh.deposit12h, sh.deposited12h) : ''}</td>
                    <td style={{ ...weekTd, color: 'var(--dim)' }}>{sh ? rateCell(sh.deposited12h, sh) : ''}</td>
                    <td style={weekTd}>{sh ? moneyCell(sh.deposit36h, sh.deposited36h) : ''}</td>
                    <td style={{ ...weekTd, color: 'var(--dim)' }}>{sh ? rateCell(sh.deposited36h, sh) : ''}</td>
                  </tr>
                );
              })}
              <tr>
                <td style={{ ...weekTd, textAlign: 'left', fontWeight: 700, borderBottom: 'none' }}>Toplam</td>
                <td style={{ ...weekTd, fontWeight: 700, borderBottom: 'none' }}>{week.totals.calls}</td>
                <td style={{ ...weekTd, fontWeight: 700, borderBottom: 'none' }}>{week.totals.answered}</td>
                <td
                  style={{ ...weekTd, fontWeight: 700, borderBottom: 'none' }}
                  title={week.totals.dials != null ? 'Arama ' + week.totals.dials + ' · bağlanan ' + (week.totals.connected ?? 0) : undefined}
                >
                  {week.totals.talkSec != null ? fmtHMS(week.totals.talkSec) : ''}
                </td>
                <td style={{ ...weekTd, fontWeight: 700, borderBottom: 'none' }}>
                  {sheetOk && week.totals.sheet ? week.totals.sheet.conversations : ''}
                </td>
                <td style={{ ...weekTd, fontWeight: 700, borderBottom: 'none', color: '#37c46b' }}>
                  {sheetOk && week.totals.sheet ? moneyCell(week.totals.sheet.deposit12h, week.totals.sheet.deposited12h) : ''}
                </td>
                <td style={{ ...weekTd, fontWeight: 700, borderBottom: 'none' }}>
                  {sheetOk && week.totals.sheet ? rateCell(week.totals.sheet.deposited12h, week.totals.sheet) : ''}
                </td>
                <td style={{ ...weekTd, fontWeight: 700, borderBottom: 'none', color: '#37c46b' }}>
                  {sheetOk && week.totals.sheet ? moneyCell(week.totals.sheet.deposit36h, week.totals.sheet.deposited36h) : ''}
                </td>
                <td style={{ ...weekTd, fontWeight: 700, borderBottom: 'none' }}>
                  {sheetOk && week.totals.sheet ? rateCell(week.totals.sheet.deposited36h, week.totals.sheet) : ''}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {sheetNote && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '10px', fontSize: '11.5px', color: 'var(--dim)' }}>
          <span>{sheetNote}</span>
          {canRetry && (
            <button
              onClick={onRetry}
              style={{
                background: 'transparent',
                border: '1px solid var(--border)',
                borderRadius: '7px',
                padding: '4px 9px',
                color: 'var(--dim)',
                fontSize: '11px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Yenile
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function DayView({
  day,
  week,
  weekState,
  onRetryWeek,
}: {
  day: AgentDayDetail;
  week: AgentWeek | null;
  weekState: WeekState;
  onRetryWeek: () => void;
}) {
  const sys = getStatusSystems();
  const smap: Record<string, StatusView> = {};
  (day.agent.lang === 'en' ? sys.en : sys.tr).forEach((x) => (smap[x.key] = x));
  const st = (key: string): StatusView => smap[key] || { label: key, color: 'var(--dim)' };

  // Çağrı günlüğü dışa aktarma — ekrandaki tabloyla AYNI sütunlar/etiketler; CSV ve XLSX
  // aynı matristen üretilir. Dosya adı: cagri-gunlugu-<ad>-<tarih>.
  const logMatrix = (): (string | number)[][] => {
    const head = ['Saat', 'Liste', 'Musteri ID', 'Sonuc', 'Yorum', 'Konusma', 'Kaynak'];
    return [head as (string | number)[]].concat(
      day.log.map((c) => [
        c.at, c.list, c.customerId, st(c.result).label, c.comment,
        c.talkSec != null ? fmtHMS(c.talkSec) : '',
        c.source === 'manual' ? 'Manuel' : 'Araç',
      ]),
    );
  };
  const logFileStem = () => 'cagri-gunlugu-' + day.agent.name.replace(/[^\p{L}\p{N}]+/gu, '-').replace(/^-|-$/g, '') + '-' + day.date;
  const onLogCsv = () => downloadCSV(logFileStem() + '.csv', logMatrix());
  const onLogXlsx = () => downloadXLSX(logFileStem() + '.xlsx', logMatrix(), day.date);

  const s = day.summary;
  const dk = day.dark;
  // BetBooker DARK kullanmaz — DARK'lı markası olmayan temsilcide DARK KOD rozeti
  // ve DARK karşılaştırma kartı hiç çizilmez (bkz. constants.ts DARK_BRANDS).
  const agentHasDark = hasDarkBrand(day.agent.brands);
  // Vardiya planı: yalnızca planı olan markalarda (BetBooker) dolu gelir.
  const sch = day.schedule;
  // Gelen çağrı görevi rozeti vardiya rozetinin YERİNE geçer (Canlı Durum kartıyla aynı
  // kural): o gün santralden çağrı alındıysa "hiç çağrı yok" tablosunun açıklaması budur.
  const schMeta = sch ? (INBOUND_DUTY_META[sch.inboundDuty] || SHIFT_META[sch.shift]) : null;

  return (
    <>
      {/* üst kart */}
      <div
        style={{
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: '14px',
          padding: '18px 20px',
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'center',
          gap: '18px',
          flexWrap: 'wrap',
        }}
      >
        <div
          style={{
            width: '52px',
            height: '52px',
            borderRadius: '14px',
            background: 'var(--accent-soft)',
            border: '1px solid var(--accent-border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flex: 'none',
          }}
        >
          <Icon name="support_agent" size={27} color="var(--accent)" />
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '19px', fontWeight: 700, color: 'var(--text)', letterSpacing: '-.01em' }}>{day.agent.name}</span>
            {day.agent.department && <span style={{ fontSize: '12px', color: 'var(--dim)' }}>{day.agent.department}</span>}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '6px', flexWrap: 'wrap' }}>
            {day.agent.brands.map((b) => (
              <span
                key={b}
                style={{
                  fontSize: '10.5px',
                  fontWeight: 600,
                  color: 'var(--dim)',
                  background: 'var(--surface2)',
                  border: '1px solid var(--border)',
                  borderRadius: '6px',
                  padding: '3px 8px',
                }}
              >
                {brandLabel(b)}
              </span>
            ))}
            {agentHasDark && (
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '5px',
                  fontSize: '10.5px',
                  fontWeight: 600,
                  color: 'var(--accent)',
                  background: 'var(--accent-soft)',
                  border: '1px solid var(--accent-border)',
                  borderRadius: '6px',
                  padding: '3px 8px',
                  letterSpacing: '.04em',
                }}
              >
                <Icon name="tag" size={13} />
                DARK KOD {day.agent.darkCode}
              </span>
            )}
            {sch && schMeta && (
              // O GÜNÜN vardiya planı (bugünün değil) — agent_schedule_days her günü saklar.
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '5px',
                  fontSize: '10.5px',
                  fontWeight: 600,
                  color: schMeta[0],
                  background: schMeta[1],
                  border: '1px solid ' + schMeta[2],
                  borderRadius: '6px',
                  padding: '3px 8px',
                  letterSpacing: '.04em',
                  fontVariantNumeric: 'tabular-nums',
                }}
                title={sch.task || sch.label}
              >
                <Icon name={schMeta[3]} size={13} />
                {/* Dinamik dizgi TEK ifade (i18n metin düğümü başına çevirir, §2). */}
                {sch.startAt && sch.endAt
                  ? (sch.inboundLabel || sch.label) + ' ' + sch.startAt + '–' + sch.endAt
                  : (sch.inboundLabel || sch.label)}
              </span>
            )}
          </div>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '22px', flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: '10px', color: 'var(--faint)', letterSpacing: '.05em', marginBottom: '3px' }}>TOPLAM ÇAĞRI</div>
            <div style={{ fontSize: '20px', fontWeight: 700, color: 'var(--text)', fontVariantNumeric: 'tabular-nums' }}>{s.totalCalls}</div>
          </div>
          <div>
            <div style={{ fontSize: '10px', color: 'var(--faint)', letterSpacing: '.05em', marginBottom: '3px' }}>İLK / SON ÇAĞRI</div>
            <div style={{ fontSize: '15px', fontWeight: 600, color: 'var(--text)', fontVariantNumeric: 'tabular-nums' }}>
              {fmtRange(s.firstCallAt, s.lastCallAt, ' – ')}
            </div>
          </div>
          <div>
            <div style={{ fontSize: '10px', color: 'var(--faint)', letterSpacing: '.05em', marginBottom: '3px' }}>TERMİNAL AÇILIŞ</div>
            <div style={{ fontSize: '15px', fontWeight: 600, color: 'var(--text)', fontVariantNumeric: 'tabular-nums' }}>{s.terminalOpenAt}</div>
          </div>
          <div>
            <div style={{ fontSize: '10px', color: 'var(--faint)', letterSpacing: '.05em', marginBottom: '3px' }}>MOLA KULLANIMI</div>
            <div style={{ fontSize: '15px', fontWeight: 600, color: '#e0a23c', fontVariantNumeric: 'tabular-nums' }}>
              {/* TEK metin düğümü olarak birleştir — i18n düğüm başına çevirir (bkz. i18n.ts) */}
              {s.breakUsedMin + ' dk · ' + s.breakCount + '×'}
            </div>
          </div>
        </div>
      </div>

      {/* haftalık istatistik — gün zaman çizelgesinin yerine (sahibin isteği 2026-09-13) */}
      <WeekCard week={week} weekState={weekState} selectedDay={day.date} onRetry={onRetryWeek} />

      {/* log + yan panel */}
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 340px', gap: '14px', alignItems: 'start' }}>
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '14px', overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '14px 18px', borderBottom: '1px solid var(--border)' }}>
            <Icon name="receipt_long" size={19} color="var(--accent)" />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '13.5px', fontWeight: 600, color: 'var(--text)' }}>Çağrı günlüğü</div>
            </div>
            <span style={{ fontSize: '12px', color: 'var(--dim)' }}>
              <span style={{ color: 'var(--text)', fontWeight: 600 }}>{day.log.length}</span> kayıt
            </span>
            {day.log.length > 0 && (
              <>
                <CsvButton onClick={onLogCsv} small />
                <XlsxButton onClick={onLogXlsx} small />
              </>
            )}
          </div>
          <div style={{ overflowX: 'auto' }}>
            <div style={{ minWidth: '730px' }}>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '60px 128px 122px 138px 1fr 64px 82px',
                  gap: '10px',
                  padding: '9px 18px',
                  borderBottom: '1px solid var(--border)',
                  fontSize: '10px',
                  fontWeight: 600,
                  letterSpacing: '.04em',
                  color: 'var(--faint)',
                }}
              >
                <span>SAAT</span>
                <span>LİSTE</span>
                <span>MÜŞTERİ ID</span>
                <span>SONUÇ</span>
                <span>YORUM</span>
                <span style={{ textAlign: 'right' }}>KONUŞMA</span>
                <span>KAYNAK</span>
              </div>
              <div style={{ maxHeight: '520px', overflowY: 'auto' }}>
                {day.log.map((c) => {
                  const manual = c.source === 'manual';
                  return (
                    <div
                      key={c.id}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '60px 128px 122px 138px 1fr 64px 82px',
                        gap: '10px',
                        padding: '10px 18px',
                        borderBottom: '1px solid var(--border)',
                        alignItems: 'center',
                      }}
                    >
                      <span style={{ fontSize: '12px', color: 'var(--dim)', fontVariantNumeric: 'tabular-nums' }}>{c.at}</span>
                      <span
                        style={{
                          fontSize: '12px',
                          color: 'var(--text)',
                          fontWeight: 500,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {c.list}
                      </span>
                      <span style={{ fontSize: '12px', color: 'var(--dim)', fontVariantNumeric: 'tabular-nums' }}>{c.customerId}</span>
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          fontSize: '12px',
                          fontWeight: 600,
                          color: st(c.result).color,
                        }}
                      >
                        <span style={dotStyle(st(c.result).color)} />
                        {st(c.result).label}
                      </span>
                      <span
                        style={{
                          fontSize: '12px',
                          color: 'var(--dim)',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {c.comment}
                      </span>
                      {/* ÖLÇÜLEN konuşma süresi (softphone); ayrıntı title'da. Oturumsuz satır BOŞ. */}
                      <span
                        title={sessionTitle(c, tzOfBrands(day.agent.brands)) || undefined}
                        style={{ textAlign: 'right', fontSize: '12px', color: 'var(--dim)', fontVariantNumeric: 'tabular-nums' }}
                      >
                        {c.talkSec != null ? fmtHMS(c.talkSec) : ''}
                      </span>
                      <span
                        style={{
                          fontSize: '11px',
                          fontWeight: 600,
                          padding: '2px 8px',
                          borderRadius: '6px',
                          whiteSpace: 'nowrap',
                          color: manual ? '#e0a23c' : 'var(--dim)',
                          background: manual ? 'rgba(224,162,60,.14)' : 'var(--surface2)',
                          border: '1px solid ' + (manual ? 'rgba(224,162,60,.3)' : 'var(--border)'),
                        }}
                      >
                        {manual ? 'Manuel' : 'Araç'}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '14px', overflow: 'hidden' }}>
            <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--border)' }}>
              <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text)' }}>Bekleme süreleri</div>
              <div style={{ fontSize: '11px', color: 'var(--faint)' }}>Sonuç tipine göre ort. / medyan</div>
            </div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 60px 60px 42px',
                gap: '6px',
                padding: '8px 16px',
                borderBottom: '1px solid var(--border)',
                fontSize: '9.5px',
                fontWeight: 600,
                letterSpacing: '.03em',
                color: 'var(--faint)',
              }}
            >
              <span>SONUÇ</span>
              <span style={{ textAlign: 'right' }}>ORT.</span>
              <span style={{ textAlign: 'right' }}>MED.</span>
              <span style={{ textAlign: 'right' }}>ADET</span>
            </div>
            {day.waitStats.map((w) => (
              <div
                key={w.result}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 60px 60px 42px',
                  gap: '6px',
                  padding: '9px 16px',
                  borderBottom: '1px solid var(--border)',
                  alignItems: 'center',
                }}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: '7px', minWidth: 0 }}>
                  <span style={dotStyle(w.color, 8)} />
                  <span style={{ fontSize: '12px', color: 'var(--text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {w.label}
                  </span>
                </span>
                <span style={{ textAlign: 'right', fontSize: '12px', color: 'var(--text)', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                  {fmtMS(w.avgSec)}
                </span>
                <span style={{ textAlign: 'right', fontSize: '12px', color: 'var(--dim)', fontVariantNumeric: 'tabular-nums' }}>
                  {fmtMS(w.medianSec)}
                </span>
                <span style={{ textAlign: 'right', fontSize: '12px', color: 'var(--dim)', fontVariantNumeric: 'tabular-nums' }}>{w.count}</span>
              </div>
            ))}
          </div>

          {/* Döngü — eklentinin ölçtüğü müşteri döngüsü (2.1.0+): müşteri gösterildi → sonuç
              kaydedildi, ve iki mekanik parçası. O gün hiçbiri ölçülmediyse kart HİÇ çizilmez. */}
          {day.cycle && (day.cycle.shownToSave || day.cycle.fetchUser || day.cycle.reload) && (
            <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '14px', overflow: 'hidden' }}>
              <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--border)' }}>
                <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text)' }}>Döngü</div>
                <div style={{ fontSize: '11px', color: 'var(--faint)' }}>Eklenti ölçümü · p50 / p95</div>
              </div>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 62px 62px 42px',
                  gap: '6px',
                  padding: '8px 16px',
                  borderBottom: '1px solid var(--border)',
                  fontSize: '9.5px',
                  fontWeight: 600,
                  letterSpacing: '.03em',
                  color: 'var(--faint)',
                }}
              >
                <span>METRİK</span>
                <span style={{ textAlign: 'right' }}>P50</span>
                <span style={{ textAlign: 'right' }}>P95</span>
                <span style={{ textAlign: 'right' }}>ADET</span>
              </div>
              <CycleRow label="Müşteri başına (gösterim → kayıt)" p={day.cycle.shownToSave} />
              <CycleRow label="Müşteri bilgisi getirme" p={day.cycle.fetchUser} />
              <CycleRow label="Sayfa yenileme" p={day.cycle.reload} />
            </div>
          )}

          {/* SMS günlüğü — o gün bu temsilcinin gönderdiği SMS'ler (migration 022). Her zaman
              çizilir: "0 SMS" de bir bilgidir. Saat, satırın markası diliminde. */}
          <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '14px', overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '14px 16px', borderBottom: '1px solid var(--border)' }}>
              <Icon name="sms" size={18} color="var(--accent)" />
              <div style={{ flex: 1, fontSize: '13px', fontWeight: 600, color: 'var(--text)' }}>SMS günlüğü</div>
              <span style={{ fontSize: '12px', color: 'var(--dim)' }}>
                <span style={{ color: 'var(--text)', fontWeight: 600 }}>{day.sms.length}</span> SMS
              </span>
            </div>
            {day.sms.length === 0 ? (
              <div style={{ padding: '16px', fontSize: '12.5px', color: 'var(--dim)' }}>Bu gün SMS gönderilmedi</div>
            ) : (
              <div style={{ maxHeight: '360px', overflowY: 'auto' }}>
                {day.sms.map((m) => (
                  <div key={m.id} style={{ padding: '10px 16px', borderBottom: '1px solid var(--border)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                      <span style={{ fontSize: '11.5px', color: 'var(--dim)', fontVariantNumeric: 'tabular-nums', flex: 'none' }}>
                        {fmtClock(m.at, tzOfBrands(day.agent.brands))}
                      </span>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', minWidth: 0, flex: 1 }}>
                        <span style={dotStyle(smsTemplateColor(m.templateId))} />
                        <span style={{ fontSize: '11.5px', color: 'var(--text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {smsTemplateLabel(m.templateId)}
                        </span>
                      </span>
                      <span
                        style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '11px', fontWeight: 600, color: smsOutcomeColor(m.outcome), flex: 'none' }}
                        title={m.outcome === 'failed' ? smsReasonLabel(m.reason) : ''}
                      >
                        <span style={dotStyle(smsOutcomeColor(m.outcome))} />
                        {m.outcome === 'failed' && m.reason ? smsReasonLabel(m.reason) : smsOutcomeLabel(m.outcome)}
                      </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '11px', color: 'var(--faint)', fontVariantNumeric: 'tabular-nums', flex: 'none' }}>{m.phone}</span>
                      <span style={{ fontSize: '11.5px', color: 'var(--dim)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={m.body}>
                        {m.body}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Oturum ↔ sonuç mutabakatı (softphone call_sessions). O gün hiç oturum yoksa sunucu
              null döner (eski sürüm — ölçülmedi) ve kart çizilmez: "hepsi oturumsuz" yanlış olurdu. */}
          {day.reconciliation && (
            <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '14px', padding: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                <Icon name="rule" size={18} color="var(--accent)" />
                <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text)' }}>Arama ↔ sonuç mutabakatı</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '7px 0' }}>
                <span style={{ fontSize: '12px', color: 'var(--dim)' }}>Softphone araması</span>
                <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text)', fontVariantNumeric: 'tabular-nums' }}>
                  {day.reconciliation.sessions}
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '7px 0', borderTop: '1px solid var(--border)' }}>
                <span style={{ fontSize: '12px', color: 'var(--dim)' }}>Araçla kaydedilen sonuç</span>
                <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text)', fontVariantNumeric: 'tabular-nums' }}>
                  {day.reconciliation.outcomes}
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '7px 0', borderTop: '1px solid var(--border)' }}>
                <span style={{ fontSize: '12px', color: 'var(--dim)' }}>Sonucu girilmemiş arama</span>
                <span
                  style={{
                    fontSize: '13px', fontWeight: 700, fontVariantNumeric: 'tabular-nums',
                    color: day.reconciliation.sessionsWithoutOutcomeCount ? '#e0a23c' : 'var(--text)',
                  }}
                >
                  {day.reconciliation.sessionsWithoutOutcomeCount}
                </span>
              </div>
              {day.reconciliation.sessionsWithoutOutcome.map((x) => (
                <div key={x.callId} style={{ display: 'flex', gap: '8px', padding: '3px 0 3px 10px', fontSize: '11.5px', color: 'var(--dim)', fontVariantNumeric: 'tabular-nums' }}>
                  <span style={{ flex: 'none' }}>{x.at}</span>
                  <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{x.customerId}</span>
                  <span style={{ flex: 'none' }}>{x.open ? 'Sürüyor' : x.answered ? fmtHMS(x.talkSec) : 'Cevapsız'}</span>
                </div>
              ))}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '7px 0', borderTop: '1px solid var(--border)' }}>
                <span style={{ fontSize: '12px', color: 'var(--dim)' }}>Araması görünmeyen sonuç</span>
                <span
                  style={{
                    fontSize: '13px', fontWeight: 700, fontVariantNumeric: 'tabular-nums',
                    color: day.reconciliation.outcomesWithoutSessionCount ? '#e0a23c' : 'var(--text)',
                  }}
                >
                  {day.reconciliation.outcomesWithoutSessionCount}
                </span>
              </div>
              {day.reconciliation.outcomesWithoutSession.map((x) => (
                <div key={x.id} style={{ display: 'flex', gap: '8px', padding: '3px 0 3px 10px', fontSize: '11.5px', color: 'var(--dim)', fontVariantNumeric: 'tabular-nums' }}>
                  <span style={{ flex: 'none' }}>{x.at}</span>
                  <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{x.customerId}</span>
                  <span style={{ flex: 'none', color: st(x.result).color }}>{st(x.result).label}</span>
                </div>
              ))}
            </div>
          )}

          {agentHasDark && (
          <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '14px', padding: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
              <Icon name="difference" size={18} color="var(--accent)" />
              <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text)' }}>DARK karşılaştırma</div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
              <div
                style={{
                  flex: 1,
                  textAlign: 'center',
                  padding: '10px',
                  background: 'var(--inset)',
                  border: '1px solid var(--border)',
                  borderRadius: '10px',
                }}
              >
                <div style={{ fontSize: '9.5px', color: 'var(--faint)', letterSpacing: '.05em', marginBottom: '3px' }}>TERMİNAL</div>
                <div style={{ fontSize: '20px', fontWeight: 700, color: 'var(--text)', fontVariantNumeric: 'tabular-nums' }}>{dk.terminalTotal}</div>
              </div>
              <Icon name="sync_alt" size={18} color="var(--faint)" />
              <div
                style={{
                  flex: 1,
                  textAlign: 'center',
                  padding: '10px',
                  background: 'var(--inset)',
                  border: '1px solid var(--border)',
                  borderRadius: '10px',
                }}
              >
                <div style={{ fontSize: '9.5px', color: 'var(--faint)', letterSpacing: '.05em', marginBottom: '3px' }}>DARK</div>
                <div style={{ fontSize: '20px', fontWeight: 700, color: 'var(--text)', fontVariantNumeric: 'tabular-nums' }}>{dk.darkTotal}</div>
              </div>
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '9px 0',
                borderTop: '1px solid var(--border)',
              }}
            >
              <span style={{ fontSize: '12px', color: 'var(--dim)' }}>Fark</span>
              <span
                style={{
                  fontSize: '11px',
                  fontWeight: 700,
                  padding: '2px 9px',
                  borderRadius: '999px',
                  color: dk.diff > 0 ? '#37c46b' : dk.diff < 0 ? '#ef6d6d' : 'var(--dim)',
                  background: dk.diff > 0 ? 'rgba(55,196,107,.14)' : dk.diff < 0 ? 'rgba(239,93,93,.14)' : 'var(--surface2)',
                }}
              >
                {(dk.diff > 0 ? '+' : '') + dk.diff}
              </span>
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '9px 0',
                borderTop: '1px solid var(--border)',
              }}
            >
              {/* getircall "Toplam Arama Süresi" = başlangıç→bitiş, ÇALMA DAHİL — konuşma süresi
                  değil. Karşısına softphone'un çalma + konuşma toplamı konur (aynı tanım). */}
              <span style={{ fontSize: '12px', color: 'var(--dim)' }}>DARK toplam arama süresi</span>
              <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text)', fontVariantNumeric: 'tabular-nums' }}>
                {fmtTalkLong(dk.darkTalkSec)}
              </span>
            </div>
            {dk.terminalTalkRingSec != null && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '9px 0',
                  borderTop: '1px solid var(--border)',
                }}
              >
                <span style={{ fontSize: '12px', color: 'var(--dim)' }}>Panel çalma + konuşma</span>
                <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text)', fontVariantNumeric: 'tabular-nums' }}>
                  {fmtTalkLong(dk.terminalTalkRingSec)}
                </span>
              </div>
            )}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '10px',
                paddingTop: '12px',
                borderTop: '1px solid var(--border)',
                marginTop: '3px',
              }}
            >
              <div>
                <div style={{ fontSize: '9.5px', color: 'var(--faint)', letterSpacing: '.04em', marginBottom: '4px' }}>TERMİNAL İLK/SON</div>
                <div style={{ fontSize: '12.5px', color: 'var(--text)', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                  {fmtRange(dk.terminalFirst, dk.terminalLast, ' – ')}
                </div>
              </div>
              <div>
                <div style={{ fontSize: '9.5px', color: 'var(--faint)', letterSpacing: '.04em', marginBottom: '4px' }}>DARK İLK/SON</div>
                <div style={{ fontSize: '12.5px', color: 'var(--text)', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                  {fmtRange(dk.darkFirst, dk.darkLast, ' – ')}
                </div>
              </div>
            </div>
          </div>
          )}
        </div>
      </div>
    </>
  );
}

export default function AgentDetail({ brands, focus }: { brands: BrandId[]; focus: AgentFocus | null }) {
  const roster = useRoster();
  const [adAgentId, setAdAgentId] = useState<string | null>(focus ? focus.id : null);
  // Varsayılan gün marka diliminde (BetBooker=Akra) — tarayıcı saati değil.
  const [adDate, setAdDate] = useState<string>(() => todayInTz(tzOfBrands(brands)));
  const [adDay, setAdDay] = useState<AgentDayDetail | null>(null);
  const [adLoading, setAdLoading] = useState(true);
  const [adMissing, setAdMissing] = useState(false);
  const [rosterSyncing, setRosterSyncing] = useState(false);
  const [adPickerOpen, setAdPickerOpen] = useState(false);
  const [adQuery, setAdQuery] = useState('');

  // Canlı Durum kartından gelindiyse o temsilciyi seç. Bağımlılık nonce'tur: aynı
  // temsilciye tekrar tıklanınca da çalışır, kullanıcının kendi seçimini ezmez.
  // (Rota değişince bu sayfa zaten unmount olur; ilk state de focus'u okur — bu
  // effect sayfa mount'lu kalırsa da doğru davranmak içindir.)
  const focusN = focus ? focus.n : 0;
  const focusId = focus ? focus.id : null;
  const rosterRef = useRef(roster);
  rosterRef.current = roster;
  useEffect(() => {
    if (!focusId) return;
    setAdAgentId(focusId);
    // Önbellekteki roster bu id'yi tanımıyorsa TAZELE: roster oturum boyu önbellekte
    // durur, /api/live ise 30sn'de bir tazelenir — oturum ortasında açılan provisional
    // ajan (ya da mevcut ajana eklenen yeni marka) canlı kartta vardır, roster'da yoktur.
    // Tazelenmezse aşağıdaki kapsam kararı eski listeye göre verilirdi.
    if (!rosterRef.current.some((a) => a.id === focusId)) {
      setRosterSyncing(true);
      refreshRoster()
        .catch(() => { /* eski liste kalır; kapsam kararı aşağıda zaten id'yi koruyor */ })
        .then(() => setRosterSyncing(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusN]);

  const allowed = roster.filter((a) => a.brands.some((b) => brands.includes(b)));
  const inScope = adAgentId != null && allowed.some((a) => a.id === adAgentId);
  const inRoster = adAgentId != null && roster.some((a) => a.id === adAgentId);
  // Kapsamdaki seçim aynen kullanılır. Roster'da OLUP marka filtresi dışına düşen seçim
  // listenin ilkine geri sarılır (kasıtlı: üstteki marka filtresi değişince).
  // Roster'da HİÇ olmayan id ise BAŞKA BİR AJANLA DEĞİŞTİRİLMEZ — sunucuya sorulur
  // (o zaten markaya göre kapılıyor, yetkisizse 404 verir). Aksi halde canlı karttan
  // tıklanan yeni/provisional ajan sessizce alfabetik ilk ajanın gününü gösterirdi:
  // süpervizör bir ajanın mola aşımını başkasına yazardı.
  const agentId =
    adAgentId && (inScope || !inRoster || rosterSyncing) ? adAgentId : allowed[0] ? allowed[0].id : null;
  const brandKey = brands.join(',');

  useEffect(() => {
    if (!agentId) {
      setAdDay(null);
      setAdMissing(false);
      setAdLoading(false);
      return;
    }
    let alive = true;
    setAdLoading(true);
    setAdMissing(false);
    getAgentDay(agentId, adDate).then(
      (d) => {
        if (!alive) return;
        setAdDay(d);
        setAdLoading(false);
      },
      // .catch YOKTU: 404 (bilinmeyen/kapsam dışı ajan) veya ağ hatası sonsuz iskelet +
      // yakalanmamış promise reddi bırakıyordu. Artık açık bir "bulunamadı" durumu.
      () => {
        if (!alive) return;
        setAdDay(null);
        setAdMissing(true);
        setAdLoading(false);
      },
    );
    return () => {
      alive = false;
    };
  }, [agentId, adDate, brandKey]);

  // Haftalık kart AYRI istekle: sheet tarafı soğuk haftada tarama demek (saniyeler), gün
  // görünümü onu beklemesin. 429 = haftalık rapor kapısı dolu → 'busy' + Yenile.
  const [week, setWeek] = useState<AgentWeek | null>(null);
  const [weekState, setWeekState] = useState<WeekState>('loading');
  const [weekTick, setWeekTick] = useState(0);
  useEffect(() => {
    if (!agentId) {
      setWeek(null);
      setWeekState('loading');
      return;
    }
    let alive = true;
    setWeekState('loading');
    getAgentWeek(agentId, adDate).then(
      (w) => {
        if (!alive) return;
        setWeek(w);
        setWeekState('ok');
      },
      (e: unknown) => {
        if (!alive) return;
        setWeek(null);
        setWeekState(e instanceof ApiError && e.status === 429 ? 'busy' : 'error');
      },
    );
    return () => {
      alive = false;
    };
  }, [agentId, adDate, brandKey, weekTick]);
  const retryWeek = () => setWeekTick((t) => t + 1);

  useDocumentClick(adPickerOpen, () => setAdPickerOpen(false));
  useEscape(adPickerOpen, () => setAdPickerOpen(false));

  const q = adQuery.trim().toLocaleLowerCase('tr');
  const pickerList = allowed.filter((a) => !q || a.name.toLocaleLowerCase('tr').includes(q));
  const selectedName = adDay ? adDay.agent.name : (allowed.find((a) => a.id === agentId) || {}).name || 'Temsilci seç';

  // "Temsilci yok" boş durumu, elimizde gösterilecek bir seçim VARKEN çıkmamalı:
  // önbellekte olmayan (yeni/provisional) bir ajana atlandığında allowed boş olabilir
  // ama gün verisi pekâlâ gelir.
  const noRoster = allowed.length === 0 && !agentId;

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '18px' }}>
        <div style={{ position: 'relative' }} onClick={(e) => e.stopPropagation()}>
          <button
            onClick={() => {
              setAdPickerOpen((o) => !o);
              setAdQuery('');
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              minWidth: '220px',
              padding: '9px 12px',
              background: 'var(--surface)',
              border: '1px solid var(--border)',
              borderRadius: '10px',
              cursor: 'pointer',
              textAlign: 'left',
            }}
          >
            <Icon name="person_search" size={19} color="var(--accent)" />
            <span style={{ flex: 1, fontSize: '13.5px', fontWeight: 600, color: 'var(--text)' }}>{selectedName}</span>
            <Icon name="expand_more" size={20} color="var(--faint)" />
          </button>
          {adPickerOpen && (
            <div
              style={{
                position: 'absolute',
                left: 0,
                top: 'calc(100% + 8px)',
                width: '300px',
                background: 'var(--surface)',
                border: '1px solid var(--border2)',
                borderRadius: '12px',
                padding: '8px',
                boxShadow: '0 14px 34px rgba(0,0,0,.5)',
                zIndex: 60,
                animation: 'caaFade .14s ease both',
              }}
            >
              <div style={{ position: 'relative', marginBottom: '6px' }}>
                <Icon
                  name="search"
                  size={17}
                  color="var(--faint)"
                  style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}
                />
                <input
                  value={adQuery}
                  onChange={(e) => setAdQuery(e.target.value)}
                  placeholder="Temsilci ara…"
                  style={{
                    width: '100%',
                    background: 'var(--inset)',
                    border: '1px solid var(--border)',
                    borderRadius: '9px',
                    padding: '9px 11px 9px 34px',
                    color: 'var(--text)',
                    fontSize: '13px',
                    outline: 'none',
                  }}
                />
              </div>
              <div style={{ maxHeight: '300px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                {pickerList.map((a) => (
                  <div
                    key={a.id}
                    onClick={() => {
                      setAdAgentId(a.id);
                      setAdPickerOpen(false);
                      setAdQuery('');
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      padding: '9px 11px',
                      borderRadius: '9px',
                      cursor: 'pointer',
                      background: a.id === agentId ? 'var(--accent-soft)' : 'transparent',
                    }}
                  >
                    <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text)', flex: 1 }}>{a.name}</span>
                    {a.department && <span style={{ fontSize: '11px', color: 'var(--dim)' }}>{a.department}</span>}
                    {hasDarkBrand(a.brands) && (
                      <span style={{ fontSize: '10.5px', color: 'var(--faint)', fontVariantNumeric: 'tabular-nums' }}>{a.darkCode}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Icon name="calendar_month" size={18} color="var(--faint)" />
          <input type="date" value={adDate} onChange={(e) => setAdDate(e.target.value)} style={dateInputStyle} />
        </div>
      </div>

      {!noRoster && adLoading && !adDay && !adMissing && <Skeleton />}

      {noRoster && (
        <EmptyState
          icon="person_off"
          title="Temsilci yok"
          reason={
            brands.length === 0 ? 'Üstteki marka filtresinden en az bir marka seçin.' : 'Seçili markalarda temsilci bulunamadı.'
          }
        />
      )}

      {/* Sunucu bu ajanı vermedi (404 = bilinmeyen ya da marka kapsamı dışı, veya ağ hatası).
          Sessizce BAŞKA bir ajanın günü gösterilmez — açıkça söylenir. */}
      {!noRoster && adMissing && !adLoading && (
        <EmptyState
          icon="person_off"
          title="Temsilci verisi alınamadı"
          reason="Bu temsilci kapsamınızda olmayabilir ya da kaydı bulunamadı. Listeden başka bir temsilci seçin."
        />
      )}

      {!noRoster && adDay && <DayView day={adDay} week={week} weekState={weekState} onRetryWeek={retryWeek} />}
    </>
  );
}
